/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getActiveConfigCategories } from 'src/modules/campaignConfigBuilder/slices';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useGetDefaultCustomFieldsApi } from 'src/api/customFields';
import { getTemplateDefaultValueTypes } from '../helpers';
import { DefaultCustomField } from 'src/constants/types';
import { CampaignConfigType } from '../types';
import { SelectOption } from '../components/types';

const FAKE_APPOINTMENT_TYPES: SelectOption[] = [
  { text: 'Sample Calendar 1', value: 'general' },
  { text: 'Sample Calendar 2', value: 'followup' },
  { text: 'Sample Calendar 3', value: 'assessment' },
  { text: 'Sample Calendar 4', value: 'emergency' },
];

const FAKE_GROUPS: SelectOption[] = [
  { text: 'Sample Group 1', value: 'vip', isArchived: false },
  { text: 'Sample Group 2', value: 'regular', isArchived: false },
  { text: 'Sample Group 3', value: 'new', isArchived: false },
  { text: 'Sample Group 4', value: 'inactive', isArchived: true },
  { text: 'Sample Group 5', value: 'prospects', isArchived: false },
];

const FAKE_KEYWORDS: SelectOption[] = [
  { text: 'Sample Keyword 1', value: 'hello' },
  { text: 'Sample Keyword 2', value: 'info' },
  { text: 'Sample Keyword 3', value: 'help' },
  { text: 'Sample Keyword 4', value: 'stop' },
  { text: 'Sample Keyword 5', value: 'start' },
];

/**
 * Provides the option lists + custom fields the builder needs.
 * Categories come from Redux; date-typed custom fields are fetched for the
 * current company and exposed as contact-date options + macro support.
 */
export const useConfigOptions = (type: CampaignConfigType) => {
  const categories = useSelector(getActiveConfigCategories);
  const companyId = useSelector(getCurrentCompanyId);
  const getCustomFields = useGetDefaultCustomFieldsApi();

  const [availableCustomFields, setAvailableCustomFields] = useState<DefaultCustomField[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!companyId) return;
      try {
        const result = await getCustomFields({
          companyId,
          types: getTemplateDefaultValueTypes({ type }),
        });
        if (!cancelled && result.data?.items) {
          setAvailableCustomFields(result.data.items);
        }
      } catch (error) {
        console.error('Failed to load custom fields:', error);
        if (!cancelled) setAvailableCustomFields([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, getCustomFields, type]);

  const contactDateTypes = useMemo<SelectOption[]>(
    () =>
      availableCustomFields
        .filter(field => field.name.includes('date') || field.description.toLowerCase().includes('date'))
        .map(field => ({
          text:
            field.description ||
            field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          value: field.name,
        })),
    [availableCustomFields],
  );

  const categoriesOptions = useMemo<SelectOption[]>(
    () =>
      categories
        .map((category: any) => ({ text: category.title, value: category.name }))
        .sort((a: SelectOption, b: SelectOption) => {
          if (a.value === 'custom') return 1;
          if (b.value === 'custom') return -1;
          return 0;
        }),
    [categories],
  );

  const metadataTypes = useMemo(() => contactDateTypes.map(field => field.text), [contactDateTypes]);

  return {
    categoriesOptions,
    groups: FAKE_GROUPS,
    keywords: FAKE_KEYWORDS,
    appointmentTypes: FAKE_APPOINTMENT_TYPES,
    contactDateTypes,
    availableCustomFields,
    metadataTypes,
  };
};
